import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import StageFieldsForm, { missingFields } from "@/components/pipeline/StageFieldsForm";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";

/** Shown when a deal is dropped on a stage whose required fields are missing. Cancel = card snaps back. */
export default function StageMoveDialog({ pending, fields, onCancel, onConfirm }) {
  const [values, setValues] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (pending) setValues({ ...(pending.deal.stage_fields || {}) }); }, [pending]);

  const confirm = async () => {
    const missing = missingFields(fields, values);
    if (missing.length) return toast.error(`Please fill: ${missing.map((f) => f.label).join(", ")}`);
    setBusy(true);
    const payload = Object.fromEntries(fields.map((f) => [f.key, values[f.key]]).filter(([, v]) => v != null && v !== ""));
    const ok = await onConfirm(payload);
    setBusy(false);
    if (ok) setValues({});
  };

  return (
    <Dialog open={!!pending} onOpenChange={(o) => !o && !busy && onCancel()}>
      <DialogContent data-testid="stage-move-dialog">
        <DialogHeader>
          <DialogTitle>Complete details to move this deal</DialogTitle>
          <DialogDescription asChild>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="font-medium text-slate-700">{pending?.deal.title}</span>
              <span className="text-slate-400">{pending?.from}</span><ArrowRight className="h-3.5 w-3.5 text-slate-400" />
              <span className="font-semibold text-teal">{pending?.to}</span>
            </div>
          </DialogDescription>
        </DialogHeader>
        <StageFieldsForm stage={pending?.to} fields={fields} values={values} onChange={setValues} highlightMissing />
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={busy} data-testid="stage-move-cancel">Cancel move</Button>
          <Button onClick={confirm} disabled={busy} className="bg-teal hover:bg-teal-700" data-testid="stage-move-confirm">{busy ? "Moving…" : `Move to ${pending?.to}`}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
